import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import { Box, Divider, Heading, SimpleGrid, Spacer } from "@chakra-ui/react";
import { FaFileInvoiceDollar } from "react-icons/fa";

import { AuthenticatedLayout } from "layouts";
import { useToast } from "hooks";
import { prettyMoney } from "utils";
import { Organization } from "types/organization";
import { getOrganization } from "api/webservice/organizations";
import { ProfileInformation } from "components";
import NavLink from "components/PrimaryNav/NavLink";
import OrganizationCard from "./OrganizationCard";
import OrganizationForm from "./OrganizationForm";

interface Props {
  organizationId: string;
}

const OrganizationProfile: React.FC<RouteComponentProps<Props>> = ({
  match
}: RouteComponentProps<Props>) => {
  const { organizationId } = match.params;
  const [editing, setEditing] = useState(false);
  const [organization, setOrganization] = useState<Organization>();
  const toast = useToast();

  const refresh = (): void => {
    getOrganization(organizationId)
      .then(organization => setOrganization(organization))
      .catch(err =>
        toast({
          title: "Something went wrong.",
          description: err.response.data.response,
          status: "error"
        })
      );
  };

  useEffect(() => {
    refresh();
  }, []);

  if (!organization) return null;

  return (
    <AuthenticatedLayout>
      <SimpleGrid
        columns={1}
        gap={8}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Heading as="h1" size="lg">
          Organization
        </Heading>
        <Box maxW="630px" m="0 auto">
          <OrganizationCard organization={organization} />
          <SimpleGrid mt={8} columns={[1, 2]} gap={4}>
            <Spacer />
            <NavLink
              to={`/organization/${organizationId}/invoices`}
              label="View Invoices"
              bg="gray.700"
              icon={FaFileInvoiceDollar}
            />
          </SimpleGrid>
        </Box>
        <Divider />
        <Box
          borderRadius="xl"
          width="100%"
          p={8}
          bg="gray.800"
          boxShadow="0px 4px 7px -1px rgba(0, 0, 0, 0.25)"
        >
          {editing ? (
            <OrganizationForm
              organization={organization}
              callback={() => {
                refresh();
                setEditing(false);
              }}
            />
          ) : (
            <ProfileInformation
              btnLabel="Edit Organization"
              callback={() => setEditing(true)}
              feilds={[
                {
                  label: "Name",
                  value: organization.name
                },
                {
                  label: "Display Name",
                  value: organization.displayName || ""
                },
                {
                  label: "Hourly Rate",
                  value: prettyMoney(organization.rate)
                },
                {
                  label: "Billing Address",
                  value: organization.billingAddress || ""
                }
              ]}
            />
          )}
        </Box>
      </SimpleGrid>
    </AuthenticatedLayout>
  );
};

export default OrganizationProfile;
